import React, { useState } from 'react';
import { Button, Table, Badge } from 'react-bootstrap';
import moment from 'moment';
import OrderSummary from '../../dashboard/components/order-summary';
import PassbookService from '../../../services/passbook.service';
import Loader from '../../../shared/common/loader';
import { formatAmount } from '../../../utils';
import { userStore } from 'store/UserStore';
import './table.css';

interface PropsType {
  metalData: any[];
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface NormalizedRow {
  ebOrderRef: string;
  createdAtUnix: number;
  quotationTotal: number;
  status: string;
  type: string;
  paymentMethod: string;
  customerId: string;
  items: any[];
  isGrouped: boolean;          // true  → eb-r-* parent
  subOrders?: any[];           // child orders for grouped rows
  _raw: any;                   // original record — passed to OrderSummary
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Pull the first item + first itemBreakDown entry safely.
 * Works for both single orders (items[]) and grouped parents (orders[].items[]).
 */
const getFirstItem = (row: NormalizedRow) => {
  if (row.isGrouped && row.subOrders?.length) {
    return row.subOrders[0]?.items?.[0]?.itemBreakDown?.[0] ?? null;
  }
  return row.items?.[0]?.itemBreakDown?.[0] ?? null;
};

/**
 * Build human-readable order description.
 */
const buildOrderDescription = (
  row: NormalizedRow,
  customerId: string
): string => {
  const breakDown = getFirstItem(row);
  const qty  = breakDown ? formatAmount(breakDown.qty, 4) : '0.0000';
  const name = breakDown?.name ?? 'Metal';


  const { type, paymentMethod, customerId: orderCustomerId } = row;

  if (type === 'SELL') {
    return `Bought ${qty} grams of ${name}`;
  }

  if (paymentMethod === 'GIFT') {
    return orderCustomerId === customerId
      ? `Gifted ${qty} grams of ${name}`
      : `Received Gift ${qty} grams of ${name}`;
  }

  return `Sold ${qty} grams of ${name}`;
};

/**
 * Normalize both single orders (eb-o-*) and grouped orders (eb-r-*)
 * into a single flat shape so the table renders consistently.
 */
const normalizeRow = (record: any): NormalizedRow => {
  const isGrouped = Boolean(record.orders); // grouped = has sub-orders array

  if (isGrouped) {
    // Aggregate amount + pick first sub-order's paymentMethod / customerId
    const firstSub = record.orders?.[0];
    return {
      ebOrderRef:     record.ebOrderRef,
      createdAtUnix:  record.createdAtUnix ?? record.createdAt,
      quotationTotal: record.orderQuotationValue ?? record.quotationTotal ?? 0,
      status:         firstSub?.status ?? record.status ?? 'UNKNOWN',
      type:           firstSub?.type ?? 'SELL',
      paymentMethod:  firstSub?.order?.paymentMethod ?? '',
      customerId:     firstSub?.customerId ?? '',
      items:          [], // not used directly for grouped rows
      isGrouped:      true,
      subOrders:      record.orders ?? [],
      _raw:           record,
    };
  }

  return {
    ebOrderRef:     record.ebOrderRef,
    createdAtUnix:  record.createdAtUnix ?? record.createdAt,
    quotationTotal: record.quotationTotal ?? record.orderQuotationValue ?? 0,
    status:         record.status ?? 'UNKNOWN',
    type:           record.type ?? 'SELL',
    paymentMethod:  record.order?.paymentMethod ?? '',
    customerId:     record.customerId ?? '',
    items:          record.items ?? [],
    isGrouped:      false,
    _raw:           record,
  };
};

// ---------------------------------------------------------------------------
// Sorting hook
// ---------------------------------------------------------------------------
type SortConfig = { key: keyof NormalizedRow; direction: 'ascending' | 'descending' } | null;

const useSortableData = (items: NormalizedRow[], config: SortConfig = null) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>(config);

  const sortedItems = React.useMemo(() => {
    const sortable = [...items];
    if (sortConfig) {
      sortable.sort((a, b) => {
        const av = a[sortConfig.key];
        const bv = b[sortConfig.key];
        if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'ascending' ? 1  : -1;
        return 0;
      });
    }
    return sortable;
  }, [items, sortConfig]);

  const requestSort = (key: keyof NormalizedRow) => {
    setSortConfig(prev =>
      prev?.key === key && prev.direction === 'ascending'
        ? { key, direction: 'descending' }
        : { key, direction: 'ascending' }
    );
  };

  const getClassNamesFor = (key: keyof NormalizedRow) =>
    sortConfig?.key === key ? sortConfig.direction : undefined;

  return { items: sortedItems, requestSort, getClassNamesFor };
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const MetalTransactions: React.FC<PropsType> = ({ metalData }) => {
  const [customer, user, customerId] = userStore((state: any) => [
    state.customer,
    state.user,
    state.customerId,
  ]);

  const [selectedMetalData, setSelectedMetalData] = useState<any>(null);
  const [isShow, setIsShow]       = useState(false);
  const [loading, setLoading]     = useState(false);

  // Normalize every record once
  const normalized: NormalizedRow[] = React.useMemo(
    () => (metalData ?? []).map(normalizeRow),
    [metalData]
  );

  const { items, requestSort, getClassNamesFor } = useSortableData(normalized);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------
  const onChangeMetalData = async (row: NormalizedRow) => {
    // For grouped orders pass the first sub-order ref so the detail API works
    const ref = row.isGrouped
      ? row.subOrders?.[0]?.ebOrderRef ?? row.ebOrderRef
      : row.ebOrderRef;
    await getOrderDetails(ref);
  };

  const getOrderDetails = async (orderRef: string) => {
    try {
      setLoading(true);
      const res: any = await PassbookService.getOrderDetails(orderRef, user?.userId);
      if (res.status === 200 && res.data?.data?.result?.length > 0) {
        setSelectedMetalData(res.data.data.result[0]);
        setIsShow(true);
      }
    } catch (e: any) {
      console.error('[MetalTransactions] getOrderDetails:', e.message);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      {selectedMetalData && (
        <OrderSummary
          handleModal={setIsShow}
          isShow={isShow}
          data={selectedMetalData}
          isPassbookRedirect={false}
          displaySummary={true}
          displayInvoice={
            customer?.customerCategory !== 'REGISTERED' ||
            selectedMetalData?.type !== 'BUYBACK'
          }
        />
      )}

      {loading && <Loader />}

      <Table className='mb-0 w-100 txn__table' striped hover responsive>
        <thead>
          <tr>
            <th className='min'>
              <button
                type='button'
                onClick={() => requestSort('ebOrderRef')}
                className={getClassNamesFor('ebOrderRef')}
              >
                Order ID
              </button>
            </th>
            <th>Type</th>
            <th>Order</th>
            <th>
              <button
                type='button'
                onClick={() => requestSort('createdAtUnix')}
                className={getClassNamesFor('createdAtUnix')}
              >
                Date
              </button>
            </th>
            <th>Status</th>
            <th className='min'>
              <button
                type='button'
                onClick={() => requestSort('quotationTotal')}
                className={getClassNamesFor('quotationTotal')}
              >
                Amount
              </button>
            </th>
            <th></th>
          </tr>
        </thead>

        <tbody>
          {items?.length === 0 && (
            <tr>
              <td className='text-center' colSpan={7}>
                No metal transactions available
              </td>
            </tr>
          )}

          {items?.map((row, index) => (
            <tr key={`${row.ebOrderRef}-${index}`}>

              {/* Order ID — badge for grouped refs */}
              <td className='min'>
                {row.ebOrderRef}
                {row.isGrouped && (
                  <Badge bg='info' className='ms-1' style={{ fontSize: '0.65rem' }}>
                    {row.subOrders?.length ?? 0} orders
                  </Badge>
                )}
              </td>

              {/* Type */}
              <td>
                {row.paymentMethod === 'GIFT'
                  ? 'GIFT'
                  : row.type === 'SELL'
                  ? 'BUY'
                  : 'SELL'}
              </td>

              {/* Order description */}
              <td>{buildOrderDescription(row, customerId)}</td>

              {/* Date */}
              <td className='min'>
                {moment(row.createdAtUnix).format('DD-MMM-YYYY  hh:mm:ss A')}
              </td>

              {/* Status */}
              <td>
                <div className='badge bg-secondary'>
                  {row.status.split('_').join(' ')}
                </div>
              </td>

              {/* Amount */}
              <td className='min'>₹ {formatAmount(row.quotationTotal)}</td>

              {/* Action */}
              <td>
                <Button
                  variant='primary'
                  onClick={() => onChangeMetalData(row)}
                  bsPrefix='btn-table'
                  size='sm'
                >
                  View
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
};

export default MetalTransactions;